// @flow
import * as React from 'react';
import Modal from 'react-modal';
import idx from 'idx';

import { withRouter } from 'app/context/Router.jsx';
import {
  setAutoMessageStatus,
  duplicateAutoMessage,
  deleteAutoMessage,
} from 'admin/actions/AutoMessageActions';
import { statusToTextBadge } from 'admin/constants/AutoMessages';
import AdminAutoMessagesNull from './AdminAutoMessagesNull.jsx';
import AdminAutoMessagesDeleteConfirmDialog from './AdminAutoMessagesDeleteConfirmDialog.jsx';
import AdminNewAutoMessageModal from 'admin/components/AdminNewAutoMessage';
import Button from 'components/Button.jsx';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';
import {
  ComposableTable,
  composeMultipleCells,
  getErrorTextCellGenerator,
  getMenuButtonCellGenerator,
  getTextCellGenerator,
  getTimestampCellGenerator,
  getTextBadgeCellGenerator,
} from 'components/ComposableTable';
import ErrorReporter from 'app/components/ErrorReporter.jsx';
import Heading from 'components/Heading.jsx';
import MediaObject from 'components/MediaObject.jsx';
import Row from 'components/Row.jsx';
import ToastActions from 'actions/ToastActions';

import type { AdminAutoMessages_organizations } from './queries/__generated__/AdminAutoMessages';

type Props = {
  loading: ?boolean,
  refetchAutoMessagesForOrg: ?() => void,
  organization: AdminAutoMessages_organizations,
  saveAutoMessage: (input: SaveAutoMessageInput) => Promise<*>,
  onClickNewAutoMessage: () => void,
  urlSlug: string,
  router: *,
};

type State = {
  isConfirmDeleteDialogOpened: boolean,
  isNewAutoMessageModalOpened: boolean,
  autoMessageModalMessageUuid: ?string,
  autoMessageModalStartingTab: ?*,
  deleteAutoMessageUuid: ?string,
  deleteAutoMessageName: ?string,
};

class AdminAutoMessagesView extends React.Component<Props, State> {
  state = {
    isNewAutoMessageModalOpened: false,
    isConfirmDeleteDialogOpened: false,
    autoMessageModalMessageUuid: undefined,
    autoMessageModalStartingTab: undefined,
    deleteAutoMessageUuid: undefined,
    deleteAutoMessageName: undefined,
  };

  showAutoMessageDialog = (
    autoMessageModalMessageUuid: string,
    autoMessageModalStartingTab?: *
  ) => {
    this.setState(
      {
        autoMessageModalMessageUuid,
        autoMessageModalStartingTab,
      },
      () => this.setState({ isNewAutoMessageModalOpened: true })
    );
  };

  hideAutoMessageDialog = () => {
    this.setState({
      autoMessageModalStartingTab: undefined,
      autoMessageModalMessageUuid: undefined,
      isNewAutoMessageModalOpened: false,
    });
  };

  showConfirmDeleteDialog = (deleteAutoMessageUuid: string, deleteAutoMessageName: string) => {
    this.setState(
      {
        deleteAutoMessageUuid,
        deleteAutoMessageName,
      },
      () => this.setState({ isConfirmDeleteDialogOpened: true })
    );
  };

  hideConfirmDeleteDialog = () => {
    this.setState({
      deleteAutoMessageUuid: undefined,
      deleteAutoMessageName: undefined,
      isConfirmDeleteDialogOpened: false,
    });
  };

  handleDelete = () => {
    const { deleteAutoMessageUuid, deleteAutoMessageName } = this.state;
    if (deleteAutoMessageUuid) {
      deleteAutoMessage(
        deleteAutoMessageUuid,
        deleteAutoMessageName || 'This message',
        this.props.organization.uuid,
        () => undefined,
        () => this.hideConfirmDeleteDialog()
      );
      return;
    }
    console.error('AdminAutoMessagesView - tried to delete an auto message without a uuid');
    ToastActions.showErrorToast('Oops! Looks like something went wrong.');
  };

  goToMessageDetail = (uuid: string) => {
    this.props.router.push(`${this.props.urlSlug}/${uuid}`);
  };

  formatStudentAndParentLabel = (includeParents: boolean, includeStudents: boolean) => {
    const parentText = includeParents ? 'Parents and guardians' : null;
    const studentText = includeStudents ? 'Students' : null;
    return [studentText, parentText].filter(text => text).join(', ');
  };

  formatDataIntoRow = (message: *) => {
    const {
      isActivatable,
      previousSends,
      permissions,
      messageName,
      messageContent,
      csvName,
      organization,
      includeParents,
      includeStudents,
      status,
      sender,
      uuid,
    } = message;
    const openModalAction = (e, step: *) => {
      e.stopPropagation();
      this.showAutoMessageDialog(uuid, step);
    };
    const emptyRecipientsButton = (
      <Button
        data-test="empty-recipients-button"
        level="icon"
        color="brand"
        onClick={e => openModalAction(e, 'compose')}>
        Assign message recipients
      </Button>
    );
    const emptyCsvNameButton = (
      <Button
        data-test="empty-csv-name-button"
        level="icon"
        color="brand"
        onClick={e => openModalAction(e, 'format-csv')}>
        Add filename
      </Button>
    );
    const lastSendTime = idx(previousSends, _ => _.edges[0].node.sentAt);

    const lastSendErrorCount =
      idx(previousSends, _ => _.edges[0].node.fileUpload.studentIdList.unmatchedStudentsCount) || 0;
    const lastSendErrorLabel =
      lastSendErrorCount > 0 ? `${lastSendErrorCount} errors` : 'No errors';

    return {
      uuid,
      messageName,
      includeParents,
      includeStudents,
      csvName: csvName || emptyCsvNameButton,
      orgUuid: organization.uuid,
      orgName: organization.name,
      recipientLabel:
        this.formatStudentAndParentLabel(includeParents, includeStudents) || emptyRecipientsButton,
      sender,
      status,
      messageContent,
      lastSendTime,
      lastSendErrorCount,
      lastSendErrorLabel: lastSendTime ? lastSendErrorLabel : 'No sends yet',
      canEdit: permissions ? permissions.canEdit : false,
      canActivate: isActivatable && status !== 'active',
      canPause: status === 'active',
      onRowClick: () => this.goToMessageDetail(uuid),
    };
  };

  render() {
    const { loading, organization, onClickNewAutoMessage } = this.props;

    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    if (!organization) {
      return (
        <ErrorReporter error="AdminAutoMessagesView - tried to render without an organization" />
      );
    }

    if (organization && organization.autoMessages.length === 0) {
      return (
        <AdminAutoMessagesNull
          showStudentIdPercentageWarning={organization.studentIdPercentage < 0.5}
          onClickNewAutoMessage={onClickNewAutoMessage}
        />
      );
    }

    const columnHeaders = ['Name', 'Status', 'Recipients', 'Last send', ''];
    const columnGenerators = [
      composeMultipleCells([
        getTextCellGenerator('messageName', {
          lineHeight: 'xlarge',
          size: 'small',
          weight: 'semibold',
          element: 'div',
        }),
        getTextCellGenerator('csvName', { color: 'thunder', size: 'xxsmall', element: 'div' }),
      ]),
      getTextBadgeCellGenerator('status', statusToTextBadge),
      composeMultipleCells([
        getTextCellGenerator('orgName', {
          lineHeight: 'xlarge',
          size: 'small',
          element: 'div',
        }),
        getTextCellGenerator('recipientLabel', {
          color: 'thunder',
          size: 'xxsmall',
          element: 'div',
        }),
      ]),
      composeMultipleCells([
        getTimestampCellGenerator('lastSendTime', {
          lineHeight: 'xlarge',
          size: 'small',
          element: 'div',
        }),
        getErrorTextCellGenerator('lastSendErrorLabel', 'lastSendErrorCount', { color: 'thunder' }),
      ]),
      getMenuButtonCellGenerator([
        {
          conditionToCheck: 'canActivate',
          label: 'Activate',
          glyph: 'play',
          fieldsForAction: ['uuid', 'messageName'],
          onClickAction: data =>
            setAutoMessageStatus(data.uuid, 'active', organization.uuid, data.messageName),
        },
        {
          conditionToCheck: 'canPause',
          label: 'Pause',
          glyph: 'pause',
          fieldsForAction: ['uuid', 'messageName'],
          onClickAction: data =>
            setAutoMessageStatus(data.uuid, 'inactive', organization.uuid, data.messageName),
        },
        {
          conditionToCheck: 'canEdit',
          label: 'Edit',
          glyph: 'pencil-stroke',
          fieldsForAction: ['uuid'],
          onClickAction: data => this.showAutoMessageDialog(data.uuid),
        },
        {
          conditionToCheck: undefined,
          label: 'Duplicate',
          glyph: 'drag-and-drop',
          fieldsForAction: [
            'messageName',
            'orgUuid',
            'includeParents',
            'includeStudents',
            'messageContent',
            'sender',
          ],
          onClickAction: data => duplicateAutoMessage(data, organization.uuid),
        },
        {
          conditionToCheck: undefined,
          label: 'View sent messages',
          glyph: 'email-transcript',
          fieldsForAction: ['uuid'],
          onClickAction: data => this.goToMessageDetail(data.uuid),
        },
        {
          conditionToCheck: 'canEdit',
          label: 'Delete',
          glyph: 'trash',
          fieldsForAction: ['uuid', 'messageName'],
          onClickAction: data => this.showConfirmDeleteDialog(data.uuid, data.messageName),
          divided: true,
        },
      ]),
    ];

    const rows = organization.autoMessages.map(message => this.formatDataIntoRow(message));

    return (
      <React.Fragment>
        <Modal isOpen={this.state.isNewAutoMessageModalOpened}>
          <AdminNewAutoMessageModal
            onBack={this.hideAutoMessageDialog}
            organizationUUID={organization.uuid}
            messageUuid={this.state.autoMessageModalMessageUuid}
            startingTab={this.state.autoMessageModalStartingTab}
          />
        </Modal>
        <Modal isOpen={this.state.isConfirmDeleteDialogOpened}>
          <AdminAutoMessagesDeleteConfirmDialog
            messageName={this.state.deleteAutoMessageName}
            onConfirm={this.handleDelete}
            onCancel={this.hideConfirmDeleteDialog}
          />
        </Modal>
        <Row size="small">
          <MediaObject figure="right" center>
            <Heading flush>Auto messages</Heading>
            <Button data-test="new-auto-message-button" onClick={onClickNewAutoMessage}>
              New auto message
            </Button>
          </MediaObject>
        </Row>
        <ComposableTable
          columnHeaders={columnHeaders}
          columnGenerators={columnGenerators}
          rows={rows}
          keyField="uuid"
          rightAlignedLastColumn
        />
      </React.Fragment>
    );
  }
}

export default withRouter(AdminAutoMessagesView);
